






























































import { defineComponent, ref, useContext, onMounted, watch } from '@nuxtjs/composition-api'

// Zoom
import VuePhotoZoomPro from 'vue-photo-zoom-pro'

// Common
import { getCloudflareImage } from '@/common/utils/images'

// Types
import {
  ImageEnhancementConfigInterface,
  ImageEnhancementZoomImagesInterface,
} from '~/types/image-enhancement/imageEnhancementTypes'

export default defineComponent({
  name: 'ImageEnhancementImageSlider',

  components: {
    VuePhotoZoomPro,
  },

  props: {
    config: {
      type: Object as () => ImageEnhancementConfigInterface,
      required: true,
    },
  },

  emits: ['onChangeSlider', 'onZoomButtonClick'],

  setup(props, { emit }) {
    const ctx = useContext()
    const imageEnhancementCaroussel: any = ref(null)
    const counter = ref(0)
    const circleWidth = ref(300)
    const loaded = ref(false)
    const zoomLoading = ref(false)
    const slickOptions = {
      dots: false,
      arrows: false,
      infinite: false,
      draggable: true,
      swipe: true,
    }

    watch(
      () => props.config.zoomEnabled,
      () => {
        if (props.config.zoomEnabled === true) {
          zoomLoading.value = true
          const figure = props.config.images.zoomImagesArray[props.config.currentSlide]
          const imgUrl = getCloudflareImage(4, figure)
          loadImagesListener(imgUrl, false)
        }
      }
    )

    // LISTEN TO EVENT FROM FOOTER COMPONENT TO TRIGGER THIS
    const onChangeIndex = (_oldSlideIndex: number, newSlideIndex: number) => {
      emit('onChangeSlider', newSlideIndex)
    }

    const loadImagesListener = (imgUrl: string, count: boolean) => {
      const img = new Image()
      img.src = imgUrl
      img.addEventListener(
        'load',
        () => {
          // When in a loop, increase the counter
          if (count) {
            counter.value++
            if (counter.value === props.config.images.zoomImagesArray.length) {
              loaded.value = true
            }
          }

          // If count is false, change zoomLoading
          zoomLoading.value = false
        },
        { once: true }
      )
    }

    const getCircleWidth = () => {
      if (process.client) {
        const width = window.innerWidth
        switch (true) {
          case width < 768:
            circleWidth.value = 150
            break
          case width < 1024:
            circleWidth.value = 200
            break
          case width < 1280:
            circleWidth.value = 250
            break
          case width < 1536:
            circleWidth.value = 300
            break
          case width < 1920:
            circleWidth.value = 350
            break
          default:
            circleWidth.value = 300
            break
        }
      }
    }

    onMounted(() => {
      // Set circle width on load and on resize
      getCircleWidth()
      window.addEventListener('resize', getCircleWidth)

      // Go over all images and check if they are loaded
      // Once done, set loaded to true and show the slider
      props.config.images.zoomImagesArray.forEach((figure) => {
        const imgUrl = showOptimized(figure) ? getCloudflareImage(2, figure) : getCloudflareImage(1, figure)
        loadImagesListener(imgUrl, true)
      })
    })

    const zoomImageInfo = (index: number) => {
      return props.config.images.zoomImagesArray.filter((el) => el.index === index)
    }

    const getImageScale = (index: number) => {
      const zoomImage = zoomImageInfo(index)
      if (zoomImage.length > 0) {
        return zoomImage[0].scale
      }
      return 2
    }

    const getImageUrl = (filename: string) => {
      return ctx.$img(`/product/image/${filename}`, null, { provider: 'ms' })
    }

    const getMaxWidth = (figure: ImageEnhancementZoomImagesInterface) => {
      if (showOptimized(figure)) {
        // Cloudflare images width for variant 2X
        // Convert to rem
        return props.config.images.zoomImage2XWidth * 0.0625
      } else {
        // multiply it by 2 is too much. Pic will get blurry texts
        // Convert to rem
        return props.config.images.nonOptimizedImageWidth * 0.0625
      }
    }

    const showOptimized = (figure: ImageEnhancementZoomImagesInterface) => {
      return figure.width >= props.config.images.zoomImage2XWidth && figure.variants
    }

    return {
      loaded,
      zoomLoading,
      circleWidth,
      slickOptions,
      imageEnhancementCaroussel,
      onChangeIndex,
      zoomImageInfo,
      getImageUrl,
      getImageScale,
      getCloudflareImage,
      getMaxWidth,
      showOptimized,
    }
  },
})
